<template lang="pug">
	.region-selector-map
		.header-data(v-if="userRegions.length")
			.markers
				template(v-for="(region, i) in userRegions")
					DxButton(:text="region.name", icon="close", stylingMode="text", type="default", @click="() => removeRegion(region)")
		.map-holder
			.region-controls
				DxTextBox.region-input(ref="regionInput", :placeholder="$tCaption('Start typing to find regions..')")
				DxNumberBox.region-radius(ref="radiusInput", :format="globalRadiusFormat", :value="radius", @value-changed="v => radius = v.value")
			.map
		DxPopup(
			:visible.sync="newRegionPopup.visible",
			:drag-enabled="false",
			:close-on-outside-click="true",
			:show-title="false",
			maxWidth="500px",
			:maxHeight="currentRegion && currentRegion.searchNearbyPlaces ? 520 : 400",
			:title="$tCaption('New region')"
		)
			template(slot="content")
				DxScrollView
					.form
						.dx-fieldset.new-region(v-if="currentRegion")
							.dx-fieldset-header {{$tCaption('Add region')}}
							.dx-field
								.dx-field-label {{$tCaption('Region')}}
								.dx-field-value.header {{ currentRegion.name }}
							.dx-field
								.dx-field-label
									DxSelectBox(:data-source="radiusOptions", :value="currentRegion.radiusType", display-expr="name", value-expr="id", @value-changed="v => currentRegion.radiusType = v.value")
								.dx-field-value
									DxNumberBox(:min="0", :max="50", :show-spin-buttons="true", :value="currentRegion.selectedRadius", @valueChanged="(e) => currentRegion.selectedRadius = e.value", :format="radiusFormat")
							.dx-field
								dx-check-box(:value.sync="currentRegion.footTrafficAttribution" :text="$tCaption('Foot traffic attribution')")
							.dx-field
								dx-check-box(:value.sync="currentRegion.searchNearbyPlaces" :text="$tCaption('Search nearby places')")
							template(v-if="currentRegion.searchNearbyPlaces")
								.dx-field
									.dx-field-label {{$tCaption('Place name')}}
									.dx-field-value
										DxTextBox(:placeholder="$tCaption('Place name')", :value="currentRegion.nearbyPlaceName", @valueChanged="(v) => currentRegion.nearbyPlaceName = v.value")
								.dx-field
									.dx-field-label
										DxSelectBox(:data-source="radiusOptions", :value="currentRegion.nearbyPlaceRadiusType", display-expr="name", value-expr="id", @value-changed="v => currentRegion.nearbyPlaceRadiusType = v.value")
									.dx-field-value
										DxNumberBox(:min="0", :max="50", :show-spin-buttons="true", :value="currentRegion.selectedNearbyPlaceRadius", @valueChanged="(e) => currentRegion.selectedNearbyPlaceRadius = e.value", :format="nearbyRadiusFormat")
							.dx-field
								DxButton(:text="$tCaption('Add region')", icon="check", type="default", @click="() => addRegion(calcRadiuses(currentRegion))")
		Screener(:source="screenerSource", :cartoStyle="cartoStyle", :width="1920 / 2", :height="1280 / 2",
			@image-generated="v => { data.imageGeographies = v; data.imageGeotargeting = v; $emit('page-status', null); }",
			@start-generation="$emit('page-status', 'Processing data')",
			@bounds-changed="boundsChanged")
</template>

<script>
	import _ from 'lodash';
	import VueTypes from 'vue-types';
	import $ from 'jquery';
	import DxTextBox from 'devextreme-vue/text-box';
	import DxButton from 'devextreme-vue/button';
	import { DxPopup } from 'devextreme-vue/popup';
	import { DxScrollView } from 'devextreme-vue/scroll-view';
	import { DxNumberBox } from 'devextreme-vue/number-box';
	import { DxCheckBox } from 'devextreme-vue/check-box';
	import DxSelectBox from 'devextreme-vue/select-box';
	import dynamic from 'dynamic';
	import api from './api.js';
	import Screener from './CartoMapScreener.vue';

	export default {
		translationCategory: 'carto.RegionSelectorMap',
		name: 'region-selector-map',
		components: { DxTextBox, DxButton, DxPopup, DxScrollView, DxNumberBox, DxCheckBox, DxSelectBox, Screener },
		model: {
			prop: 'regions',
			event: 'regions-change'
		},
		props: {
			regions: VueTypes.array.def([]),
			mapPosition: VueTypes.object.def({}),
			data: VueTypes.object.def({})
		},
		data() {
			return {
				currentRegion: void 0,
				objects: {},
				newRegionPopup: {
					visible: false
				},
				innerRegions: [],
				radiusOptions: [
					{ id: 'radius', name: () => this.$tCaption('Radius') },
					{ id: 'time', name: () => this.$tCaption('Driving time') }
				],
				screenerSource: null,
				cartoStyle: `
					#layer {
						polygon-fill: ramp([a_pop_num], (#ecda9a,#f1b973,#f7945d,#f86f56,#ee4d5a), jenks);
						polygon-opacity: 0.8;
					}
					#layer::outline {
						line-width: 0.5;
						line-color: #d4b895;
						line-opacity: 0.6;
					}
					#layer::labels [zoom>8]{
						text-name: [zcta5ce10];
						text-face-name: 'DejaVu Sans Book';
						text-size: 10;
						text-fill: #000000;
						text-label-position-tolerance: 0;
						text-halo-radius: 1;
						text-halo-fill: #FFFFFF;
						text-opacity: 0.5;
						text-dy: -10;
						text-allow-overlap: false;
						text-placement: point;
						text-placement-type: dummy;
					}
				`,
				radius: 0
			};
		},
		computed: {
			userRegions() {
				return this.innerRegions.filter(f => !!f.isManuallyAdded);
			},
			isMetricSystem() {
				return api.isMetricSystem();
			},
			metricMultiplier() {
				return api.metricMultiplier();
			},
			radiusFormat() {
				return `#0 ${this.currentRegion.radiusType === 'time' ? 'min' : (this.isMetricSystem ? 'km' : 'mi')}`;
			},
			nearbyRadiusFormat() {
				return `#0 ${this.currentRegion.nearbyPlaceRadiusType === 'time' ? 'min' : (this.isMetricSystem ? 'km' : 'mi')}`;
			},
			globalRadiusFormat() {
				return `${this.$tCaption('Radius')}: #0 ${this.radius > 0 ? api.kmOrMiles() : this.$tCaption('(whole area)')}`;
			}
		},
		mounted() {
			api.clearClient();
			if (__DEBUG__)
				console.log('mounted');
			window.jQuery = $;
			dynamic.loadOnce([
				'https://maps.googleapis.com/maps/api/js?key=AIzaSyBxX6xc0TryC8oGKqVLaIIyJT9w46OYsLE&libraries=places,geometry&language=en&js',
				'https://libs.cartocdn.com/carto.js/v4.2.0/carto.min.js',
				'https://carto.com/developers/carto-js/examples/maps/public/style.css'
			]).then(() => {
				this.buildMap();
				this.regions.map(f => this.addRegion(f, true));
			});
		},
		destroyed() {
			this.$map = null;
		},
		methods: {
			boundsChanged(cartoBounds) {
				const google = window.google;
				var bounds = new google.maps.LatLngBounds();
				cartoBounds.map(f => {
					bounds.extend(new google.maps.LatLng(f[0], f[1]));
				});

				if (__DEBUG__)
					console.debug('Bounds changed', bounds);
				this.$map.fitBounds(bounds);
			},
			calcRadiuses(region) {
				//average car speed in city https://www.statista.com/statistics/264703/average-speed-in-europes-15-most-congested-cities/
				var kmh = 35;
				if (region.radiusType === 'radius') {
					region.radius = region.selectedRadius * api.metricMultiplier();
				} else {
					region.radius = Number(parseFloat(region.selectedRadius * api.metricMultiplier() * kmh / 60).toFixed(0));
				}

				if (region.nearbyPlaceRadiusType === 'radius') {
					region.nearbyPlaceRadius = region.selectedNearbyPlaceRadius * api.metricMultiplier();
				} else {
					region.nearbyPlaceRadius = Number(parseFloat(region.selectedNearbyPlaceRadius * api.metricMultiplier() * kmh / 60).toFixed(0));
				}

				if (__DEBUG__)
					console.info('Radiuses calculated', region);
				return region;
			},
			goNext() {
				if (this.$map && this.$map.center)
					this.$emit('map-position-change', { zoom: this.$map.zoom, lat: this.$map.center.lat(), lng: this.$map.center.lng() });
				this.$emit('next');
			},
			generateGeoJSONCircle(center, radius, numSides) {
				numSides = numSides || 16;
				var points = [],
					degreeStep = 360 / numSides;

				const google = window.google;

				for(var i = 0; i < numSides; i++){
					var gpos = google.maps.geometry.spherical.computeOffset(center, radius, degreeStep * i);
					points.push([gpos.lng(), gpos.lat()]);
				};

				// Duplicate the last point to close the geojson ring
				points.push(points[0]);

				return {
					type: 'Polygon',
					coordinates: [ points ]
				};
			},
			addObject(region, object, removeObject) {
				if (!this.objects[region.id])
					this.objects[region.id] = [];

				this.objects[region.id].push({ object, removeObject });
			},
			removeObjects(region) {
				if (this.objects[region.id])
					this.objects[region.id].map(f => f.removeObject && f.removeObject(f.object));
				delete this.objects[region.id];
			},
			getObjects(region) {
				return this.objects[region.id] || [];
			},
			addRegion(region, silent) {
				if (region.searchNearbyPlaces) {
					if (region.radius <= 0 || region.nearbyPlaceRadius <= 0) {
						this.$notify(this.$tCaption('You must specify both radiuses'), 'error', 3000);
						return;
					}

					if (!region.nearbyPlaceName) {
						this.$notify(this.$tCaption('You must specify place name'), 'error', 3000);
						return;
					}
				}

				this.newRegionPopup.visible = false;
				this.innerRegions.push(region);

				if (__DEBUG__)
					console.info('Add region', {region, newRegions: this.innerRegions});

				if (!silent) {
					region.location = {
						lat: region.geometry.location.lat(),
						lng: region.geometry.location.lng()
					};

					if (region.searchNearbyPlaces)
						this.addRadiusRegion(region);

					if (region.address_components) {
						region.state = region.address_components.filter(f => f.types.indexOf('administrative_area_level_1') > -1)[0];
						region.country = region.address_components.filter(f => f.types.indexOf('country') > -1)[0];
						region.county = region.address_components.filter(f => f.types.indexOf('administrative_area_level_2') > -1)[0];
						region.city = region.address_components.filter(f => f.types.indexOf('locality') > -1)[0];
					}

					region.geoData = this.getObjects(region).filter(f => f.object.center && f.object.radius).map(f => this.generateGeoJSONCircle(f.object.center, f.object.radius));
				}

				this.addPointRegion(region, region.opts);

				if (!silent)
					this.$emit('regions-change', this.innerRegions);

				if (__DEBUG__)
					console.info('New regions', this.innerRegions);

				this.buildZipsLayer(true);
			},
			addPointRegion(region, options) {
				if (__DEBUG__)
					console.info('Add point region', {region, options}, JSON.stringify(region.geometry.location));

				const google = window.google;
				const opts = {
					markerIcon: 'http://maps.google.com/mapfiles/ms/icons/orange-dot.png',
					showMarker: true,
					...options
				};

				var location = new google.maps.LatLng(region.location.lat, region.location.lng);
				if (opts.showMarker) {
					if (__DEBUG__)
						console.log('Add marker');

					var marker = new google.maps.Marker({
						position: location,
						animation: google.maps.Animation.DROP,
						icon: opts.markerIcon
					});

					this.addObject(region, marker, (m) => m.setMap(null));
					setTimeout(() => {
						marker.setMap(this.$map);
					}, 100);
				}

				if (region.radius && !region.searchNearbyPlaces)
					this.buildRadiusArea(region);
			},
			addRadiusRegion(radiusRegion) {
				if (__DEBUG__)
					console.info('Add radius region', radiusRegion, JSON.stringify(radiusRegion.geometry.location));

				const google = window.google;
				const search = {
					radius: radiusRegion.radius,
					location: radiusRegion.geometry.location,
					fields: ['name', 'geometry'],
					query: radiusRegion.nearbyPlaceName
				};

				if (__DEBUG__)
					console.info('Search nearby region', {radiusRegion, search});

				this.$places.textSearch(search, (results, status) => {
					if (status === google.maps.places.PlacesServiceStatus.OK) {
						if (__DEBUG__)
							console.info('Nearby results found', results);

						const places = results.filter(f =>
							f.name.toLowerCase().indexOf(radiusRegion.nearbyPlaceName.toLowerCase()) > -1
							&& google.maps.geometry.spherical.computeDistanceBetween(f.geometry.location, radiusRegion.geometry.location) <= radiusRegion.radius);
						places.map(f => {
							f.footTrafficAttribution = radiusRegion.footTrafficAttribution;
							f.radius = radiusRegion.nearbyPlaceRadius;
							f.address_components = radiusRegion.address_components;
							f.parentRegionId = radiusRegion.id;
							f.opts = { showMarker: false };
							this.addRegion(f);
						});

						this.$notify(`${places.length} ` + this.$tCaption('places founded'), places.length ? 'success' : 'error', 3000);
					}
				});
			},
			buildRadiusArea(region) {
				if (!region.radius) return;
				var google = window.google;
				var location = new google.maps.LatLng(region.location.lat, region.location.lng);
				var circle = new google.maps.Circle({
					strokeColor: '#693366',
					strokeOpacity: 0.8,
					strokeWeight: 1,
					fillColor: '#693366',
					fillOpacity: 0.35,
					map: this.$map,
					center: location,
					radius: region.radius
				});

				this.addObject(region, circle, (c) => c.setMap(null));

				if (__DEBUG__)
					console.info('Bild radius area', { region, circle, type: typeof(circle) });
			},
			removeRegion(region, silent) {
				if (__DEBUG__)
					console.debug('Remove region', region, this.innerRegions);

				const regionIndex = _.indexOf(this.innerRegions, region);

				if (region.isManuallyAdded) {
					if (__DEBUG__)
						console.debug('Is manually added');
					this.innerRegions.filter(f => f && f.parentRegionId && f.parentRegionId === region.id).map(f => this.removeRegion(f, true));
				}

				this.removeObjects(region);

				this.innerRegions.splice(regionIndex, 1);

				if (!silent)
					this.$emit('regions-change', this.innerRegions);

				this.buildZipsLayer(true);
			},
			buildZipsLayer(clearData) {
				if (__DEBUG__)
					console.debug('buildZipsLayer', { regions: this.innerRegions });
				const carto = window.carto;
				const source = new carto.source.SQL(api.buildQuery(this.innerRegions));
				const client = api.getClient();

				if (clearData) {
					this.screenerSource = null;
					this.data.imageGeographies = null;
				}

				this.$map.overlayMapTypes.clear();
				if (this.$layerZipSelection) {
					client.removeLayer(this.$layerZipSelection);
					delete this.$layerZipSelection;
				}

				if (!this.innerRegions.length) return;

				if (__DEBUG__)
					console.info('Add zips layer', { carto, source, client, map: this.$map });

				const selectionZipStyle = new carto.style.CartoCSS(this.cartoStyle);

				if (!this.$layerZipSelection) {
					const layerZipSelection = this.$layerZipSelection = new carto.layer.Layer(source, selectionZipStyle, {
						featureClickColumns: ['cartodb_id', 'zcta5ce10']
					});

					client.addLayer(layerZipSelection);

					this.$map.overlayMapTypes.push(client.getGoogleMapsMapType(this.$map));
				}

				setImmediate(() => {
					if (this._isDestroyed) return;
					this.screenerSource = source;
				});
			},
			changeMapPosition() {
				if (this.$map && this.$map.center)
					this.$emit('map-position-change', { zoom: this.$map.zoom, lat: this.$map.center.lat(), lng: this.$map.center.lng() });
			},
			buildMap() {
				if (__DEBUG__)
					console.log('Build map region selector');

				const $el = this.$el;
				const google = window.google;
				var map, places;

				var autocomplete;
				var countries = {
					'us': {
						center: {lat: 37.1, lng: -95.7},
						zoom: 5
					}
				};

				var initMap = () => {

					map = new google.maps.Map($($el).find('.map')[0], {
						zoom:  this.mapPosition.zoom || countries['us'].zoom,
						center: this.mapPosition.lat ? {lat: this.mapPosition.lat, lng: this.mapPosition.lng} : countries['us'].center,
						mapTypeControl: false,
						panControl: true,
						zoomControl: true,
						streetViewControl: false
					});

					this.$map = map;
					// Create the autocomplete object and associate it with the UI input control.
					// Restrict the search to the default country, and to place type "cities".
					autocomplete = new google.maps.places.Autocomplete(
						/** @type {!HTMLInputElement} */
						($($el).find('.region-input input')[0]));
					places = new google.maps.places.PlacesService(map);

					this.$places = places;

					autocomplete.addListener('place_changed', onPlaceChanged);

					this.$map.addListener('zoom_changed', () => {
						this.changeMapPosition();
					});

					$($el).find('.region-input input').attr('placeholder', ' ');
				};

				// When the user selects a city, get the place details for the city and
				// zoom the map in on the city.
				var onPlaceChanged = () => {
					var place = autocomplete.getPlace();
					if (place.geometry) {
						map.panTo(place.geometry.location);

						this.changeMapPosition();

						if (this.$refs.regionInput && this.$refs.regionInput.instance && this.$refs.regionInput.instance._clearValue)
							this.$refs.regionInput.instance._clearValue();

						const existingRegion = _.find(this.innerRegions, f => f && f.place && f.place.place_id === place.place_id);
						if (existingRegion) {
							this.removeRegion(existingRegion, true);
						}

						this.currentRegion = {
							...place,
							radius: 0,
							footTrafficAttribution: false,
							isManuallyAdded: true,
							searchNearbyPlaces: false,
							nearbyPlaceName: '',
							nearbyPlaceRadius: 0,
							radiusType: 'radius',
							nearbyPlaceRadiusType: 'radius',
							selectedRadius: this.radius,
							selectedNearbyPlaceRadius: 0
						};

						if (this.currentRegion.address_components.length > 4) {
							this.newRegionPopup.visible = true;
						} else {
							this.addRegion(this.calcRadiuses(this.currentRegion));
						}
					}
				};

				initMap();
			}
		}
	};
</script>
<style lang='scss'>
	.new-region {
		> .dx-field > .dx-button {
			width: 100%;
		}

		.dx-field-value.header {
			white-space: nowrap;
			max-width: 60%;
			overflow: hidden;
			text-overflow: ellipsis;
		}
	}

	.region-selector-map {
		.new-region {
			.header {
				font-size: 22px;
			}
		}

		.markers {
			background: #ececec;
			padding: 2px;
			margin: 4px;
			margin-left: 10px;
			border-radius: 4px;

			> * {
				margin-right: 4px;
			}
		}

		> .map-holder {
			height: calc(100vh - 309px);
			width: 100%;
			margin-top: 0px;
			position: relative;

			> .region-controls {
				padding: 15px 15px 15px 14px;
				background: rgba(255, 255, 255, 0.95);
				border-radius: 4px;
				position: absolute;
				top: 6px;
				left: 6px;
				width: 600px;
				max-width: 80%;
				z-index: 100;
				box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
				display: flex;

				> .region-input {
					flex: 5;
					margin-right: 10px;

					&.dx-texteditor.dx-editor-underlined:after {
						border-bottom: 1px solid rgba(70, 70, 70, 0.13);
					}
				}

				> .region-add-button {
					flex-basis: 200px;
				}

				.region-radius {
					flex: 2;
				}
			}

			.map {
				width: 100%;
				height: 100%;
				min-height: 300px;
				border-radius: 4px;
			}

			.info {
				padding: 6px 8px;
				//font: 14px/16px Arial, Helvetica, sans-serif;
				background: white;
				background: rgba(255,255,255,0.8);
				box-shadow: 0 0 15px rgba(0,0,0,0.2);
				border-radius: 5px;
			}

			.legend i {
				width: 18px;
				height: 18px;
				float: left;
				margin-right: 8px;
				opacity: 0.7;
			}

			.header {
				font-size: 18px;
			}
		}
	}
</style>
